import clsx from "clsx";
import { FunctionComponent, useMemo } from "react";

import SocialLinks from "@ui/components/navigation/SocialLinks";
import SELECTORS from "@ui/config/class-selectors.config";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { type NavItem } from "@voxmedia/duet-context/context/global-data-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";

import FooterLinks from "./components/FooterLinks";
import NetworkSitesFooter from "./components/NetworkSitesFooter";
import PmcPrivacyBanner from "./components/PmcPrivacyBanner";
import * as styles from "./Footer.css";

export type LinkProps = {
  component?: React.ReactNode;
  title: string;
  href: string;
};
export interface FooterBaseConfig {
  Logo: FunctionComponent<{ className?: string }>;
  linkData: LinkProps[][];
  getLinkData?: (footerNav: NavItem[] | null) => LinkProps[][];
  showUpperContainer?: boolean;
  showBrandLinks?: boolean;
  showNetworkSites?: boolean;
  showPmcPrivacyBanner?: boolean;
  /** Brand name used in the PMC sub-footer copyright, e.g. `"Eater"`. */
  copyrightBrandName?: string;
  /** Applied to the PMC bar. Verge uses this to lock the bar to dark theme. */
  networkSitesClassName?: string;
  border?: boolean;
  adText?: string;
}

interface Props extends Partial<FooterBaseConfig> {
  className?: string;
  componentContext?: string;
}

const Footer: FunctionComponent<Props> = (props) => {
  const analytics = useAnalytics();
  const footerConfig = useComponentConfig(
    "footer",
    props.componentContext || "",
  );
  const { footerNav } = useGlobalDataContext();

  const {
    className,
    getLinkData,
    Logo,
    linkData,
    showUpperContainer,
    showBrandLinks,
    showNetworkSites,
    showPmcPrivacyBanner,
    copyrightBrandName,
    networkSitesClassName,
    border,
    adText,
  } = useDefaults(props, footerConfig);

  const links = useMemo(
    () => (getLinkData ? getLinkData(footerNav) : linkData),
    [getLinkData, footerNav, linkData],
  );

  return (
    <>
      <footer className={clsx(SELECTORS.Footer, className)}>
        {/* Note: the extra containers are because ads are dynamically injected into the footer,
        and we don't want them wrapped in the footer's bg color */}
        <div className={styles.outerContainer}>
          <div className={styles.innerContainer}>
            {showUpperContainer && (
              <div
                className={clsx(
                  styles.upperContainer,
                  border && styles.upperContainerBorder,
                )}
              >
                <a
                  href="/"
                  onClick={() =>
                    analytics?.emit({
                      event: "footer_click",
                      footer_click: { name: "Logo" },
                    })
                  }
                >
                  <Logo className={styles.logo.standard} />
                </a>
                <div
                  className={clsx(
                    styles.socialIcons,
                    border && styles.socialIconsBorder,
                  )}
                >
                  <SocialLinks />
                </div>
              </div>
            )}
            <FooterLinks showBrandLinks={showBrandLinks} linkData={links} />
            {!showNetworkSites && (
              <p className={styles.copyright}>
                © {new Date().getFullYear()}{" "}
                <a rel="nofollow" href="https://www.voxmedia.com">
                  Vox Media
                </a>
                , LLC. All Rights Reserved
              </p>
            )}

            {adText && <p className={styles.adText}>{adText}</p>}
          </div>
        </div>
        {showNetworkSites && (
          <NetworkSitesFooter
            className={networkSitesClassName}
            copyrightBrandName={copyrightBrandName}
          />
        )}
      </footer>
      {showPmcPrivacyBanner && <PmcPrivacyBanner />}
    </>
  );
};

export default Footer;
